import {
  ATTENTION_ISSUE_LABELS,
  CAUSE_PRIORITY,
  DELAY_THRESHOLD_MINUTES,
  ESCALATION_THRESHOLD_MINUTES,
  UNASSIGNED_WARNING_WINDOW_MINUTES,
  VARIANCE_LABELS,
} from "@/lib/statusDisplay"
import { type Variance, VARIANCE_DISPLAY_ORDER, varianceClasses } from "@/lib/tone"
import { DE_ESCALATION_HOLD_MINUTES } from "@/lib/varianceStability"
import { cn } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ReferenceCard, ReferenceSection } from "@/components/collections/ReferenceCard"

/** Every number and label is read from the classifying modules, never transcribed. */

const BAND_RULES: Record<Variance, string> = {
  done: "Completed. Nothing further to report, however it ran.",
  on_plan: `Running within ${DELAY_THRESHOLD_MINUTES} minutes of plan, or not yet due to start.`,
  at_risk: `Running ${DELAY_THRESHOLD_MINUTES}–${ESCALATION_THRESHOLD_MINUTES - 1} minutes behind, or missing a driver or vehicle with under ${UNASSIGNED_WARNING_WINDOW_MINUTES} minutes to go.`,
  late: `Running ${ESCALATION_THRESHOLD_MINUTES} or more minutes behind, or past its due time and never departed.`,
}

function Swatch({ variance }: { variance: Variance }) {
  return (
    <span
      className={cn(
        "inline-block h-4 w-8 flex-none rounded border border-palette-jungleGreen300 align-middle",
        varianceClasses(variance).fill,
      )}
      aria-hidden="true"
    />
  )
}

export function MonitoringReference() {
  return (
    <ReferenceCard
      title="Monitoring"
      intro="How a route is judged at risk or late, and what the summary band above the timeline and map is showing you"
    >
      <ReferenceSection
        title="What we measure"
        intro="Five inputs decide everything below. Two of them are not what you would guess."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Input</TableHead>
              <TableHead>Definition</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Due time</TableCell>
              <TableCell>
                The <strong>first stop&rsquo;s planned arrival</strong> — not the route&rsquo;s
                planned start. The start is usually a depot departure hours earlier and says
                nothing about whether a customer is waiting.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Has it started?</TableCell>
              <TableCell>
                Whether the route has a real recorded start time. The status field alone
                isn&rsquo;t trusted — a stray GPS ping can flip it to in-progress on a route
                nobody has driven.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Delay</TableCell>
              <TableCell>
                Minutes behind plan, falling back to the worst individual stop when the
                route-level figure is implausible.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Assigned?</TableCell>
              <TableCell>
                Whether the route has both a driver and a vehicle. Counts as unassigned whether
                the field is missing, blank, or literally reads &ldquo;Unassigned&rdquo;.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Load factor</TableCell>
              <TableCell>
                Percentage of vehicle capacity used. Only ever a cause, never a band — being
                overloaded is a real problem but not a schedule one.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ReferenceSection>

      <ReferenceSection
        title="The bands"
        intro="Every route sits in exactly one. Listed worst first, the order they appear in the bar — the scale is ordered throughout, which is why it can be read at a glance and still works in greyscale."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Colour</TableHead>
              <TableHead className="w-32">Band</TableHead>
              <TableHead>A route lands here when…</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {VARIANCE_DISPLAY_ORDER.map((v) => (
              <TableRow key={v}>
                <TableCell>
                  <Swatch variance={v} />
                </TableCell>
                <TableCell className="font-medium capitalize">{VARIANCE_LABELS[v]}</TableCell>
                <TableCell>{BAND_RULES[v]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReferenceSection>

      <ReferenceSection
        title="Thresholds"
        intro="Change these in code and this page updates with them — the numbers below are read from the same constants the monitoring views use."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Value</TableHead>
              <TableHead>What it governs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">
                {DELAY_THRESHOLD_MINUTES} min
              </TableCell>
              <TableCell>
                On plan becomes at risk. The same number the badge on each route row uses, so the
                band and the row can never disagree about the same route.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">
                {ESCALATION_THRESHOLD_MINUTES} min
              </TableCell>
              <TableCell>At risk becomes late.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">
                {UNASSIGNED_WARNING_WINDOW_MINUTES} min
              </TableCell>
              <TableCell>
                How close to departure a missing driver or vehicle starts counting as a problem.
                Before this, it is stated but not flagged — there is still time to fix it.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">
                {DE_ESCALATION_HOLD_MINUTES} min
              </TableCell>
              <TableCell>
                How long a route must stay improved before its band moves back down.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-xs text-palette-jungleGreen500">
          How stale the Samsara and Track-POD feeds may get, and how far out a driver-entered time
          may fall before it is discarded, are in <strong>Integrations</strong> below — they govern
          what reaches these screens rather than how a route is judged once it does.
        </p>
      </ReferenceSection>

      <ReferenceSection
        title="Why the band doesn't flicker"
        intro="A route sitting right on a threshold would otherwise flip state every time the screen refreshes, which is how a display stops being trusted."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Direction</TableHead>
              <TableHead>Behaviour</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Getting worse</TableCell>
              <TableCell>
                Applied immediately. Bad news is never held back to smooth the display.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Getting better</TableCell>
              <TableCell>
                Held for {DE_ESCALATION_HOLD_MINUTES} minutes first. A route flapping either side
                of a threshold escalates on the first crossing and stays there, because each dip
                back has to survive the hold to count.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ReferenceSection>

      <ReferenceSection
        title="Why a route is at risk or late"
        intro="Each route is counted under exactly one cause, so the breakdown on the banner always adds up to its headline figure. Resources come first because that is the order of what you can actually do — a route with no driver is also inevitably late, and calling it delayed hides the only thing anyone can fix."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Order</TableHead>
              <TableHead className="w-40">Cause</TableHead>
              <TableHead>Applies when</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {CAUSE_PRIORITY.map((kind, i) => (
              <TableRow key={kind}>
                <TableCell className="tabular-nums text-palette-jungleGreen500">{i + 1}</TableCell>
                <TableCell className="font-medium capitalize">
                  {ATTENTION_ISSUE_LABELS[kind]}
                </TableCell>
                <TableCell>
                  {kind === "no_driver" &&
                    `No driver assigned, and due within ${UNASSIGNED_WARNING_WINDOW_MINUTES} minutes or already past due.`}
                  {kind === "no_vehicle" &&
                    `No vehicle assigned, and due within ${UNASSIGNED_WARNING_WINDOW_MINUTES} minutes or already past due.`}
                  {kind === "over_capacity" && "Loaded to 100% of vehicle capacity or beyond."}
                  {kind === "late_start" && "Past its due time and has not set off."}
                  {kind === "running_late" &&
                    `Under way and ${DELAY_THRESHOLD_MINUTES} or more minutes behind.`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReferenceSection>

      <ReferenceSection
        title="Counted separately"
        intro="Three things sit outside the bands, because none of them is a step toward failure."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Signal</TableHead>
              <TableHead>Meaning</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Ahead</TableCell>
              <TableCell>
                Running {DELAY_THRESHOLD_MINUTES} or more minutes early. A deviation from plan,
                but not one on the way to a problem, so it is reported as a number rather than
                coloured as a state.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Not started</TableCell>
              <TableCell>
                On plan and not yet away — the part of the day still to come. The moment one
                passes its due time it becomes late.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Cancelled</TableCell>
              <TableCell>
                Left off the bar entirely. A cancelled route is no longer on the plan, so it has
                no variance from it.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ReferenceSection>
    </ReferenceCard>
  )
}

export default MonitoringReference
